/**
 * Selects the Automations client. Uses the live agent-trust endpoints whenever an authed workspace
 * is available; falls back to the typed fixture client only when there is no session yet (e.g. an
 * unauthenticated preview), so the surface stays renderable without a backend. The old build-time
 * `VITE_AGENTS_LIVE` gate is gone — the module is live by default.
 *
 * `scope` is folded into the query keys (workspace id when live, "fixtures" otherwise) so
 * switching workspaces refetches and the two data sources never share a cache entry.
 */
import { useMemo } from "react";
import { useSession } from "@/features/auth/hooks/useSession";
import { resolveWorkspaceId } from "@/lib/workspace-routing";
import { automationsClient, type AutomationsClient } from "./automationsClient";
import { createLiveAutomationsClient } from "./liveAutomationsClient";

export function useAutomationsClient(): { client: AutomationsClient; scope: string } {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  return useMemo(() => {
    if (token && workspaceId) {
      return {
        client: createLiveAutomationsClient(token, workspaceId),
        scope: workspaceId,
      };
    }
    return { client: automationsClient, scope: "fixtures" };
  }, [token, workspaceId]);
}
